import React, { useState, useCallback, useContext, useRef } from 'react';
import EDMenu from '../../../../../../BaseComponents/Menu/EDMenu';
import EDIconButton from '../../../../../../BaseComponents/Buttons/IconButton/EDIconButton';
import EDMenuItem from '../../../../../../BaseComponents/Menu/EDMenuItem';
import EDIcon from '../../../../../../BaseComponents/Icon/EDIcon';
import EDTypography from '../../../../../../BaseComponents/Typography/EDTypography';
import { TEDUrvalVisibility } from '../../../../../../Types/UrvalTypes';
import { UrvalContext } from '../../../../Context/UrvalContext';
import { standardGrundurvalDisabledReason } from '../../../UrvalDialog/UrvalDialog';

interface IProps {
  hasPickedSavedUrval: boolean;
  onResetUrvalClick: () => void;
  onNewSelectionClick: () => void;
  onModifyUrvalClick: () => void;
  onSaveNewUrvalClick: () => void;

  isGemensamtUrval: boolean;
  canSaveGemensammaUrval: boolean;
  availableVisibilityOptions: TEDUrvalVisibility[];
  addTab: () => void;
  disableNyUrvalsSida?: boolean;
}

const urvalToolbarSaveButtonDisabled = (
  hasPickedSavedUrval: boolean,
  isGemensamtUrval: boolean,
  canSaveGemensammaUrval: boolean,
  availableVisibilityOptions: TEDUrvalVisibility[],
  standardGrundUrval: boolean
): boolean =>
  availableVisibilityOptions.length === 0 ||
  !hasPickedSavedUrval ||
  (isGemensamtUrval && !canSaveGemensammaUrval) ||
  standardGrundUrval;

const urvalToolbarGetSaveButtonDisabledReason = (
  hasPickedSavedUrval: boolean,
  isGemensamtUrval: boolean,
  canSaveGemensammaUrval: boolean,
  availableVisibilityOptions: TEDUrvalVisibility[],
  standardGrundUrval: boolean
): string | undefined => {
  if (standardGrundUrval) {
    return standardGrundurvalDisabledReason;
  } else if (!hasPickedSavedUrval) {
    return 'Valt urval är ej ett sparat urval, använd "spara som" om du vill spara ett nytt urval';
  } else if (availableVisibilityOptions.length === 0) {
    return 'Du saknar behörighet till att spara urval';
  } else if (isGemensamtUrval && !canSaveGemensammaUrval) {
    return 'Du saknar behörighet till att spara ett gemensamt urval';
  }

  return undefined;
};

const UrvalToolbarMenu: React.FunctionComponent<
  React.PropsWithChildren<IProps>
> = props => {
  const [open, setOpen] = useState(false);
  const menuButtonRef = useRef<HTMLButtonElement>(null);
  const toggleMenu = useCallback(() => setOpen(prevOpen => !prevOpen), []);
  const closeMenu = useCallback(() => setOpen(_ => false), []);
  const clickMenuItem = (func: () => void) => () => {
    closeMenu();
    func();
  };

  const {
    hasPickedSavedUrval,
    onResetUrvalClick,
    onNewSelectionClick,
    onModifyUrvalClick,
    onSaveNewUrvalClick,
    canSaveGemensammaUrval,
    isGemensamtUrval,
    availableVisibilityOptions,
    addTab,
  } = props;

  const { standardGrundUrval } = useContext(UrvalContext);

  return (
    <>
      <EDIconButton
        tooltip="Meny"
        testId="UrvalToolbarMenu-IconButton"
        icon="more_vert"
        onClick={toggleMenu}
        color="inherit"
        padding="medium"
        iconSize="medium"
        primaryBackground
        ref={menuButtonRef}
      />
      <EDMenu
        open={open}
        onClose={closeMenu}
        anchorEl={menuButtonRef.current}
        anchorOrigin={{
          horizontal: 'center',
          vertical: 'bottom',
        }}
        transformOrigin={{
          horizontal: 'center',
          vertical: 'top',
        }}
      >
        {!props.disableNyUrvalsSida && (
          <EDMenuItem testId="MenuItem-add_tab" onClick={clickMenuItem(addTab)}>
            <EDIcon iconType="add" marginRight={1} />
            <EDTypography>Ny urvalssida</EDTypography>
          </EDMenuItem>
        )}
        <EDMenuItem
          testId="MenuItem-restore_backup"
          disabled={!hasPickedSavedUrval}
          onClick={clickMenuItem(onResetUrvalClick)}
        >
          <EDIcon iconType="restore_backup" marginRight={1} />
          <EDTypography>Återställ valt urval</EDTypography>
        </EDMenuItem>
        <EDMenuItem
          testId="MenuItem-create_new_folder"
          onClick={clickMenuItem(onNewSelectionClick)}
        >
          <EDIcon iconType="create_new_folder" marginRight={1} />
          <EDTypography>Nytt urval</EDTypography>
        </EDMenuItem>
        <EDMenuItem
          testId="MenuItem-save_alt"
          onClick={clickMenuItem(onModifyUrvalClick)}
          disabled={urvalToolbarSaveButtonDisabled(
            hasPickedSavedUrval,
            isGemensamtUrval,
            canSaveGemensammaUrval,
            availableVisibilityOptions,
            standardGrundUrval
          )}
          disabledReason={urvalToolbarGetSaveButtonDisabledReason(
            hasPickedSavedUrval,
            isGemensamtUrval,
            canSaveGemensammaUrval,
            availableVisibilityOptions,
            standardGrundUrval
          )}
        >
          <EDIcon iconType="save_alt" marginRight={1} />
          <EDTypography>Spara</EDTypography>
        </EDMenuItem>
        <EDMenuItem
          testId="MenuItem-save_as"
          onClick={clickMenuItem(onSaveNewUrvalClick)}
          disabled={availableVisibilityOptions.length === 0}
          disabledReason={'Du saknar behörighet till att spara urval'}
        >
          <EDIcon iconType="save_as" marginRight={1} />
          <EDTypography>Spara som</EDTypography>
        </EDMenuItem>
      </EDMenu>
    </>
  );
};

export default UrvalToolbarMenu;
